import roles from '@/constants/roles';
import { useAppStore } from '@/store/useAppStore';
import { resetUserSession } from '@/services/resetUserSession';
import { redirect } from '@tanstack/react-router';
import axios from 'axios';

export type TCustomError = {
  message: string;
  code: number;
  name: string;
  payload: null;
};

export type TCustomAxiosError = {
  code: number;
  data: null;
  success: boolean;
  errors: TCustomError[];
};

export type TCustomAxiosResponse<T> = {
  code: number;
  success: boolean;
  errors: TCustomError[];
  data: T;
  pagination?: {
    page: number;
    limit: number;
    totalPages: number;
    offset: number;
    totalItems: number;
  };
};

// When the dev proxy is on (default), use the relative "/api" so requests flow
// through Vite's proxy. When it's off (VITE_USE_DEV_PROXY=false), talk to the
// API directly via VITE_API_BASE_URL (requires backend CORS).
const useDevProxy = import.meta.env.VITE_USE_DEV_PROXY !== 'false';

const api = axios.create({
  baseURL:
    import.meta.env.DEV && useDevProxy ? '/api' : import.meta.env.VITE_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

let retryCount = 0;
const maxRetries = 3;
let lastRetryTime = 0;
const RETRY_RESET_TIME = 5 * 60 * 1000; // 5 minutes

api.interceptors.response.use(
  (response) => {
    retryCount = 0;
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const isPublicRequest = originalRequest?.url?.includes('/v1/public/');

    // Reset retry count if enough time has passed
    if (Date.now() - lastRetryTime > RETRY_RESET_TIME) {
      retryCount = 0;
    }

    // Only refresh on 401 (expired tokens), not 403 (business logic), and exclude login endpoints
    if (
      (!isPublicRequest &&
        error.response?.status === 401 &&
        retryCount < maxRetries &&
        !originalRequest._retry &&
        !originalRequest.url?.includes('/login')) ||
      // considers the case where a already logged in user has been upgraded to peer support,
      // but the new peer-support-including token hasn't been fetched from the back-end
      (error.response?.status === 403 && originalRequest.url?.includes('/peersupport/profile'))
    ) {
      console.log('error retry for NON pub req')
      originalRequest._retry = true;
      retryCount += 1;
      lastRetryTime = Date.now();

      try {
        await api.post('/v1/user/auth/refresh');
        return api(originalRequest);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (refreshError: any) {
        retryCount = maxRetries;
        signOutRedirectAndStoreReset(refreshError);
        return Promise.reject(refreshError);
      }
    }

    // Handle max retries reached
    if (!isPublicRequest && retryCount >= maxRetries) {
      signOutRedirectAndStoreReset(error);
    }

    if (!isPublicRequest && error.response?.status === 400) {
      const appStore = useAppStore.getState();
      const isSuperAdmin = !!appStore.user?.roles?.includes(roles.superAdmin);
      if (isSuperAdmin) {
        signOutRedirectAndStoreReset(error);
      }
    }

    if (!error.response) {
      return Promise.reject({
        code: 0,
        data: null,
        success: false,
        errors: [
          {
            message: error.message || 'Network request failed.',
            code: 0,
            name: error.name || 'NetworkError',
            payload: null,
          },
        ],
      } satisfies TCustomAxiosError);
    }

    const apiError = error.response.data as TCustomAxiosError;

    return Promise.reject(apiError);
  },
);

export { api };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const signOutRedirectAndStoreReset = (error: any) => {
  resetUserSession();

  const message = error.response?.data?.errors?.[0]?.message || error.message;
  const isInvalidCredentials = message?.includes('credentials');

  if (!isInvalidCredentials) {
    redirect({
      to: '/sign-in',
      replace: true,
    });
    window.location.reload();
  }
};
